import React, { useCallback, useEffect, useState } from 'react'
import styles from './ScrollDots.module.css'

const EDGE_PX = 4
const MAX_DOTS = 7
const DOT_PX = 9
const GAP_PX = 7
const PILL_PX = 26
const STEP_PX = DOT_PX + GAP_PX

const ScrollDots = ({
    targetRef,
    watch,
    maxDots = MAX_DOTS,
    className = '',
}) => {
    const [pages, setPages] = useState(1)
    const [active, setActive] = useState(0)

    const measure = useCallback(() => {
        const el = targetRef.current
        if (!el) return
        const view = el.clientHeight
        const max = el.scrollHeight - view
        if (view <= 0 || max <= EDGE_PX) {
            setPages(1)
            setActive(0)
            return
        }
        const total = Math.ceil(el.scrollHeight / view)
        setPages(total)
        if (el.scrollTop <= EDGE_PX) return setActive(0)
        if (el.scrollTop >= max - EDGE_PX) return setActive(total - 1)
        setActive(Math.round((el.scrollTop / max) * (total - 1)))
    }, [targetRef])

    useEffect(() => {
        const el = targetRef.current
        if (!el) return undefined
        let frame = 0
        const schedule = () => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(measure)
        }
        schedule()
        el.addEventListener('scroll', schedule, { passive: true })
        const observer =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(schedule)
                : null
        if (observer) {
            observer.observe(el)
            Array.from(el.children).forEach((node) => observer.observe(node))
        }
        return () => {
            cancelAnimationFrame(frame)
            el.removeEventListener('scroll', schedule)
            observer?.disconnect()
        }
    }, [targetRef, measure, watch])

    const goTo = (i) => {
        const el = targetRef.current
        if (!el) return
        const max = el.scrollHeight - el.clientHeight
        const top = (max * i) / Math.max(1, pages - 1)
        el.scrollTo({ top, behavior: 'smooth' })
    }

    const visible = pages > 1
    const shown = Math.min(pages, maxDots)
    const start = Math.min(
        Math.max(active - Math.floor(shown / 2), 0),
        pages - shown
    )
    const end = start + shown - 1
    const isEdge = (i) =>
        (i === start && start > 0) || (i === end && end < pages - 1)

    return (
        <nav
            className={`${styles.dots} ${
                visible ? styles.visible : ''
            } ${className}`}
            style={{
                '--dot': `${DOT_PX}px`,
                '--gap': `${GAP_PX}px`,
                '--pill': `${PILL_PX}px`,
                '--window': `${shown * STEP_PX - GAP_PX + PILL_PX - DOT_PX}px`,
            }}
            aria-hidden={!visible}
        >
            <div className={styles.window}>
                <div
                    className={styles.track}
                    style={{ transform: `translateY(${-start * STEP_PX}px)` }}
                >
                    {Array.from({ length: pages }, (_, i) => {
                        const inView = i >= start && i <= end
                        return (
                            <button
                                key={i}
                                type="button"
                                tabIndex={visible && inView ? 0 : -1}
                                className={`${styles.dot} ${
                                    i === active ? styles.active : ''
                                } ${isEdge(i) ? styles.edge : ''}`}
                                onClick={() => goTo(i)}
                                title={`Page ${i + 1} of ${pages}`}
                                aria-label={`Go to page ${i + 1}`}
                                aria-current={i === active ? 'true' : undefined}
                            />
                        )
                    })}
                </div>
            </div>
        </nav>
    )
}

export default ScrollDots
